import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=900&q=80',
  'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=900&q=80',
  'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=900&q=80',
  'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=900&q=80',
  'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=900&q=80',
  'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&q=80',
];

const ServicesSection = () => {
  const { t, currentLanguage } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/services/catalog`, {
          params: { lang: currentLanguage },
        });
        if (cancelled) return;
        const cats = Array.isArray(res?.data?.data) ? res.data.data : [];
        setItems(cats.slice(0, 6));
      } catch (err) {
        console.error('Failed to load services:', err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  return (
    <section id="services" className="section-padding section-tone-a">
      <div className="container-premium">
        <div className="section-head">
          <span className="section-eyebrow">{t('services.eyebrow', 'What we do')}</span>
          <h2 className="section-title">{t('services.title', 'Premium Dental Services')}</h2>
          <p className="section-sub">
            {t('services.subtitle', 'Comprehensive dental solutions using the latest technology and premium materials.')}
          </p>
        </div>

        {loading ? (
          <div className="grid-3 services-grid">
            {[0, 1, 2].map((i) => (
              <div key={i} className="service-card-rich is-skeleton">
                <div className="service-thumb skeleton-block" />
                <div className="service-content">
                  <div className="skeleton-line w-30" />
                  <div className="skeleton-line w-80" />
                  <div className="skeleton-line w-60" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? null : (
          <div className="grid-3 services-grid">
            {items.map((s, idx) => {
              const img = s.image_url || FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length];
              return (
                <Link
                  key={s.id || s.slug}
                  to={`/services/${s.slug}`}
                  className="service-card-rich"
                >
                  <div className="service-thumb-wrap">
                    <img src={img} alt={s.title} loading="lazy" />
                    <span className="service-pill">
                      <Sparkles size={12} />
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="service-content">
                    <h3>{s.title}</h3>
                    {s.short_description && <p>{s.short_description}</p>}
                    <span className="service-link">
                      {t('services.learnMore', 'Learn more')} <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="section-foot">
          <Link to="/services" className="btn-crystal-outline">
            {t('services.viewAll', 'View All Services')} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
